import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './contexts/AppContext.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import MainLayout from './layouts/MainLayout.tsx';
import LoginPage from './pages/LoginPage.tsx';
import PayrollPage from './pages/PayrollPage.tsx';
import TeamsPage from './pages/TeamsPage.tsx';
import TeamPerformancePage from './pages/TeamPerformancePage.tsx';
import ActualSalesManagementPage from './pages/ActualSalesManagementPage.tsx';
import SettingsPage from './pages/SettingsPage.tsx';
import DailyReportPage from './pages/DailyReportPage.tsx';
import AuthTestPage from './pages/AuthTestPage.tsx';
import UserManagementPage from './pages/UserManagementPage.tsx';
import ProfilePage from './pages/ProfilePage.tsx';
import WebhookExample from './components/WebhookExample.js';
import SessionTimeoutProvider from './components/SessionTimeoutProvider.tsx';
import { logger } from './utils/logger.ts';
import './App.css';

// React.lazy를 사용한 동적 임포트로 코드 스플리팅 최적화
const AnnualLeavePage = React.lazy(() => import('./pages/AnnualLeavePage.tsx'));

// 로딩 컴포넌트
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// 실제 라우팅 관리 컴포넌트
const AppRoutes = () => {
  const location = useLocation();
  
  // 경로 변경 시 로그 기록
  useEffect(() => {
    logger.debug('현재 경로: ' + location.pathname);
  }, [location]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth-test" element={<AuthTestPage />} />
      
      {/* Protected routes - 기본 페이지를 팀 성과 관리로 변경 */}
      <Route path="/" element={
        <ProtectedRoute permissions={['view_performance']}>
          <MainLayout>
            <TeamPerformancePage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/payroll" element={
        <ProtectedRoute permissions={['view_payroll']}>
          <MainLayout>
            <PayrollPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/teams" element={
        <ProtectedRoute permissions={['view_teams']}>
          <MainLayout>
            <TeamsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/team-performance" element={
        <ProtectedRoute permissions={['view_performance']}>
          <MainLayout>
            <TeamPerformancePage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/actual-sales" element={
        <ProtectedRoute permissions={['view_sales']}>
          <MainLayout>
            <ActualSalesManagementPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/daily-report" element={
        <ProtectedRoute permissions={['view_daily_report']}>
          <MainLayout>
            <DailyReportPage />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/annual-leave" element={
        <ProtectedRoute permissions={['view_annual_leave']}>
          <MainLayout>
            <Suspense fallback={<LoadingSpinner />}>
              <AnnualLeavePage />
            </Suspense>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute permissions={['view_settings']}>
          <MainLayout>
            <SettingsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* User Management page */}
      <Route path="/user-management" element={
        <ProtectedRoute permissions={['manage_users']}>
          <MainLayout>
            <UserManagementPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Profile page - 모든 로그인한 사용자가 접근 가능 */}
      <Route path="/profile" element={
        <ProtectedRoute permissions={[]}>
          <MainLayout>
            <ProfilePage />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Webhook Example page */}
      <Route path="/webhook-example" element={
        <ProtectedRoute permissions={[]}>
          <MainLayout>
            <WebhookExample />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Auth test page - no protection for testing */}
      <Route path="/auth-test" element={<AuthTestPage />} />
      
      {/* Catch all - redirect to team performance */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <SessionTimeoutProvider>
        <AppProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AppProvider>
      </SessionTimeoutProvider>
    </AuthProvider>
  );
}

export default App;